// Gestore per i modelli remoti dal server
// URL base: https://iscag-ei.it/wp-content/materiale/animazioni-3d/

import {ASSETS_CONFIG} from './assets-config'
import {discoverModels, addDiscoveredModels} from './model-discovery'

// Configurazioni predefinite per diversi tipi di modelli
export const MODEL_CATEGORIES = {
  // Statue e sculture
  statues: {
    venere: { title: 'Venere di Milo', scale: '2 2 2', position: '0 0 -8' },
    david: { title: 'David di Michelangelo', scale: '1.8 1.8 1.8', position: '0 0 -8' },
    apollo: { title: 'Apollo del Belvedere', scale: '2.2 2.2 2.2', position: '0 0 -8' }
  },
  
  // Personaggi animati
  characters: {
    soldato: { title: 'Soldato Romano', scale: '2 2 2', position: '0 0 0' },
    gladiatore: { title: 'Gladiatore', scale: '2 2 2', position: '0 0 0' },
    imperatore: { title: 'Imperatore', scale: '2.5 2.5 2.5', position: '0 0 0' }
  },
  
  // Edifici e architettura
  buildings: {
    colosseo: { title: 'Colosseo', scale: '0.5 0.5 0.5', position: '0 0 -15' },
    pantheon: { title: 'Pantheon', scale: '0.6 0.6 0.6', position: '0 0 -15' },
    castello: { title: 'Castello Medievale', scale: '0.8 0.8 0.8', position: '0 0 -12' }
  },
  
  // Veicoli
  vehicles: {
    nave: { title: 'Nave Romana', scale: '0.8 0.8 0.8', position: '0 0 -10' },
    automobile: { title: 'Automobile Antica', scale: '1 1 1', position: '0 0 -8' },
    carro: { title: 'Carro Romano', scale: '1.2 1.2 1.2', position: '0 0 -8' }
  },
  
  // Oggetti e strumenti
  objects: {
    strumento: { title: 'Strumento Musicale', scale: '0.5 0.5 0.5', position: '0 1 -3' },
    arma: { title: 'Arma Antica', scale: '0.4 0.4 0.4', position: '0 1 -3' },
    vaso: { title: 'Vaso Antico', scale: '0.6 0.6 0.6', position: '0 0 -5' }
  }
}

// Funzione per aggiungere un modello con configurazione automatica
export const addModelWithAutoConfig = (modelName, category = 'objects') => {
  const categoryConfig = MODEL_CATEGORIES[category] || MODEL_CATEGORIES.objects
  const modelConfig = categoryConfig[modelName] || categoryConfig[Object.keys(categoryConfig)[0]]
  
  // Aggiungi il modello alla configurazione
  ASSETS_CONFIG.addModel(modelName, `${modelName}.glb`)
  
  // Restituisci la configurazione completa per l'esperienza
  return {
    modelType: category === 'characters' ? 'tutaAmianto' : 'generic',
    modelPath: ASSETS_CONFIG.models[modelName],
    title: modelConfig.title,
    description: `Modello 3D di ${modelConfig.title}`,
    scale: modelConfig.scale,
    position: modelConfig.position,
    customParams: {
      category: category,
      autoConfigured: true
    }
  }
}

// Funzione per aggiungere modelli in batch
export const addModelsBatch = (modelNames, category = 'objects') => {
  const configs = []
  
  modelNames.forEach(modelName => {
    const config = addModelWithAutoConfig(modelName, category)
    configs.push(config)
  })
  
  console.log(`📦 Aggiunti ${configs.length} modelli in batch`)
  return configs
}

// Funzione per creare esperienze da modelli scoperti
export const createExperiencesFromDiscovered = async () => {
  console.log('🔍 Scoperta modelli e creazione esperienze...')
  
  const discovered = await discoverModels()
  const experiences = []
  
  discovered.forEach(model => {
    // Determina la categoria basandosi sul nome
    let category = 'objects'
    if (model.name.includes('soldato') || model.name.includes('personaggio')) {
      category = 'characters'
    } else if (model.name.includes('colosseo') || model.name.includes('edificio')) {
      category = 'buildings'
    } else if (model.name.includes('nave') || model.name.includes('auto')) {
      category = 'vehicles'
    } else if (model.name.includes('venere') || model.name.includes('statua')) {
      category = 'statues'
    }
    
    const config = addModelWithAutoConfig(model.name, category)
    experiences.push(config)
  })
  
  console.log(`🎉 Create ${experiences.length} esperienze dai modelli scoperti`)
  return experiences
}

// Funzione per ottenere tutti i modelli disponibili organizzati per categoria
export const getModelsByCategory = () => {
  const categorized = {}
  
  Object.keys(MODEL_CATEGORIES).forEach(category => {
    categorized[category] = []
    
    Object.keys(MODEL_CATEGORIES[category]).forEach(modelName => {
      if (ASSETS_CONFIG.models[modelName]) {
        categorized[category].push({
          name: modelName,
          config: MODEL_CATEGORIES[category][modelName],
          url: ASSETS_CONFIG.models[modelName]
        })
      }
    })
  })
  
  return categorized
}

// Funzione per suggerire modelli basati su testo
export const suggestModelsByText = (searchText) => {
  const suggestions = []
  const searchLower = searchText.toLowerCase()
  
  Object.entries(MODEL_CATEGORIES).forEach(([category, models]) => {
    Object.entries(models).forEach(([modelName, config]) => {
      if (modelName.toLowerCase().includes(searchLower) || 
          config.title.toLowerCase().includes(searchLower)) {
        suggestions.push({
          name: modelName,
          category: category,
          config: config,
          url: ASSETS_CONFIG.models[modelName] || `${ASSETS_CONFIG.baseUrl}/${modelName}.glb`
        })
      }
    })
  })
  
  return suggestions
}

// Funzione per inizializzare il sistema con modelli remoti
export const initializeRemoteModels = async () => {
  console.log('🚀 Inizializzazione sistema modelli remoti...')
  
  // Aggiungi modelli predefiniti
  Object.keys(MODEL_CATEGORIES).forEach(category => {
    Object.keys(MODEL_CATEGORIES[category]).forEach(modelName => {
      if (!ASSETS_CONFIG.models[modelName]) {
        ASSETS_CONFIG.addModel(modelName, `${modelName}.glb`)
      }
    })
  })
  
  // Scopri modelli aggiuntivi
  await addDiscoveredModels()
  
  console.log('✅ Sistema modelli remoti inizializzato')
  return getModelsByCategory()
} 